import {Breadcrumb, Modal, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {DisplayContent, DisplayPreReqContent} from '@/containers';
import {getDisplayContentLanguagesService} from '@/services/languageService';
import {MediaCardUiSchema} from '@/services/schema/mediaCardJsonSchema';
import {useAppDispatch, useAppSelector} from '@/store';
import {setLanguages, setToastNotifications} from '@/store/slices/commonSlice';
import {
  resetSalesTarget,
  setSalesTargetDisplayContent,
  setSalesTargetDisplayContentMedia,
  setSalesTargetChildDisplayContent,
  setSalesTargetDisplayContentMediaOptional,
  setSalesTargetFormData
} from '@/store/slices/salesTargetSlice';
import {logger} from '@/helpers/logger';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {ROUTES} from '@/utils/constants/routesConstants';
import {IMAGETYPE, TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {getStepConfig} from '@/utils/salesTargetStepConfig';
import {Constants, STATUS_VALUES} from '@/utils/constants/commonConstants';
import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {
  getSalesTargetActiveWindowDates,
  hasInvalidImageCombination,
  isNowWithinDateWindow
} from './SalesTargetUtils';
import {patchSalesTarget} from '@/services/salesTargets';
import {transformMediaArray} from '@/pages/SalesTargetUtils';

export const AddSalesTargetDisplayContent = () => {
  const location = useLocation();
  const isEdit = location.state?.isEdit ?? false;
  const disableDraft = location.state?.disableDraft ?? false;
  const editPublishedBeforeStart =
    location.state?.editPublishedBeforeStart ?? false;
  const editMode = location.state?.editMode;
  const isEditDisplayContent = editMode === 'displayContent';
  const returnTo = location.state?.returnTo || ROUTES.SALES_TARGET_REVIEW;
  const salesTargetIdFromState = location.state?.salesTargetId;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const salesTarget = useAppSelector((state) => state.salesTarget);
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const countryLanguages = COUNTRY_LANGUAGES_CODES[countryCode];
  const [isCancel, setIsCancel] = useState(false);
  const displayContentLanguages = useAppSelector(
    (state) => state.common.languages
  );
  const [activeLanguage, setActiveLanguage] = useState('en');
  const {handleDraft} = useSalesTargets(false);
  const [errorMessageMedia, setErrorMessageMedia] = useState(false);
  const displayContentMedia = useAppSelector(
    (state) => state.salesTarget.displayContentMedia
  );
  const displayContentMediaOptional = useAppSelector(
    (state) => state.salesTarget.displayContentMediaOptional
  );

  const {start: activeStartDate, end: activeEndDate} =
    getSalesTargetActiveWindowDates(salesTarget);
  const isActiveSalesTargetWindow = isNowWithinDateWindow(
    activeStartDate,
    activeEndDate
  );

  const normalizeStatus = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z]/g, '');

  const isInProgressStatus =
    normalizeStatus(salesTarget?.status) ===
    normalizeStatus(Constants.InProgress);

  const isPublishedStatus =
    normalizeStatus(salesTarget?.status) ===
    normalizeStatus(Constants.Published);

  const isPublishedBeforeStart = useMemo(() => {
    if (!isPublishedStatus) return false;
    const startDate = activeStartDate;
    if (!startDate) return false;
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return false;
    return new Date() < start;
  }, [isPublishedStatus, activeStartDate]);

  const canPatchDisplayContentNow =
    isEditDisplayContent &&
    (isInProgressStatus || isActiveSalesTargetWindow || isPublishedBeforeStart);

  const [displayContentFormData, setDisplayContentFormData] = useState(() => {
    const languages =
      COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
    const formData = {};

    languages.forEach((lang) => {
      const code = lang?.code;
      formData[code] = {
        targetType: salesTarget?.targetType,
        ...salesTarget?.displayContent?.[code]
      };
    });
    return formData;
  });

  const [displayPreReqContentFormData, setDisplayPreReqContentFormData] =
    useState(() => {
      const languages =
        COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
      const parentData = {};
      const childData = {};

      languages.forEach((lang) => {
        const code = lang.code;
        parentData[code] = {
          targetType: salesTarget?.targetType,
          ...salesTarget?.displayContent?.[code]
        };
        childData[code] = {
          targetType: salesTarget?.targetType,
          ...salesTarget?.child?.displayContent?.[code]
        };
      });

      return {
        ...parentData,
        child: childData
      };
    });

  const [mediaCardFormData, setMediaCardFormData] = useState({
    title: salesTarget?.displayContentMedia?.title ?? salesTarget?.targetType,
    imageId: salesTarget?.displayContentMedia?.imageId ?? '',
    imageUri: salesTarget?.displayContentMedia?.imageUri ?? '',
    imageBase64: salesTarget?.displayContentMedia?.imageBase64 ?? '',
    optIn: salesTarget?.displayContentMedia?.optIn ?? '',
    imageUrl: salesTarget?.displayContentMedia?.imageUrl
  });

  const isPrerequisiteTarget =
    salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite ||
    salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite;

  const isEarlyPaymentTarget = useMemo(
    () => salesTarget?.targetType === TARGET_TYPES.EarlyPaymentTarget,
    [salesTarget?.targetType]
  );

  const isDropSizeTarget = useMemo(
    () => salesTarget?.targetType === TARGET_TYPES.DropSizeTarget,
    [salesTarget?.targetType]
  );

  const isLinkedTargetType = useMemo(
    () => isEarlyPaymentTarget || isDropSizeTarget,
    [isEarlyPaymentTarget, isDropSizeTarget]
  );

  const stepConfig = useMemo(
    () => getStepConfig(salesTarget?.targetType),
    [salesTarget?.targetType]
  );

  const persistDisplayContentMediaToRedux = () => {
    dispatch(
      setSalesTargetDisplayContentMedia({
        title: salesTarget?.displayContentMedia?.title,
        imageId: salesTarget?.displayContentMedia?.imageId,
        imageUri: salesTarget?.displayContentMedia?.imageUri,
        imageBase64: salesTarget?.displayContentMedia?.imageBase64,
        optIn: salesTarget?.displayContentMedia?.optIn,
        imageUrl: salesTarget?.displayContentMedia?.imageUrl
      })
    );
    dispatch(
      setSalesTargetDisplayContentMediaOptional({
        title: salesTarget?.displayContentMediaOptional?.title,
        imageId: salesTarget?.displayContentMediaOptional?.imageId,
        imageUri: salesTarget?.displayContentMediaOptional?.imageUri,
        imageBase64: salesTarget?.displayContentMediaOptional?.imageBase64,
        optIn: salesTarget?.displayContentMediaOptional?.optIn,
        imageUrl: salesTarget?.displayContentMediaOptional?.imageUrl
      })
    );
  };

  const persistDisplayContentToRedux = () => {
    if (isPrerequisiteTarget) {
      dispatch(setSalesTargetDisplayContent(displayPreReqContentFormData));
      dispatch(
        setSalesTargetChildDisplayContent(displayPreReqContentFormData.child)
      );
    } else {
      dispatch(setSalesTargetDisplayContent(displayContentFormData));
    }
    persistDisplayContentMediaToRedux();

    // Preserve form data fields (startDate, endDate, etc.) to prevent data loss
    dispatch(setSalesTargetFormData(salesTarget.formData || salesTarget));
  };

  const handleNextAction = async () => {
    persistDisplayContentToRedux();

    const route = isLinkedTargetType
      ? ROUTES.SALES_TARGET_REVIEW
      : ROUTES.SALES_TARGET_PRODUCTS;

    navigate(route, {
      state: {
        isEdit: isEdit,
        disableDraft,
        editPublishedBeforeStart,
        salesTargetId: salesTargetIdFromState || salesTarget?.id
      }
    });
  };

  const buildDisplayContentUpdatePayload = () => {
    const media = transformMediaArray(
      [displayContentMedia, displayContentMediaOptional].filter(
        (m) => m?.imageUri
      )
    );

    if (isPrerequisiteTarget) {
      const parentDisplayContent = Object.fromEntries(
        Object.entries(displayPreReqContentFormData).filter(
          ([key]) => key !== 'child'
        )
      );

      return {
        displayContent: parentDisplayContent,
        child: {
          displayContent: displayPreReqContentFormData.child
        },
        media,
        status: STATUS_VALUES.Published
      };
    }

    return {
      displayContent: displayContentFormData,
      media,
      status: STATUS_VALUES.Published
    };
  };

  const handleSaveAndPublishEditDisplayContent = async () => {
    try {
      persistDisplayContentToRedux();

      // Only patch display content when editing an active (in-progress) sales target.
      // For published-before-start edit wizard, changes should be patched once on Review submit.
      if (!canPatchDisplayContentNow) {
        navigate(returnTo);
        return;
      }

      const id = salesTargetIdFromState || salesTarget?.id;
      if (!id) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Sales Target id is missing',
            type: 'error'
          })
        );
        return;
      }

      const payload = buildDisplayContentUpdatePayload();
      const response = await patchSalesTarget(id, payload);
      if (response?.success === false) {
        dispatch(
          setToastNotifications({
            show: true,
            message: response?.message || 'Failed to update display content',
            type: 'error'
          })
        );
        return;
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: 'Display Content updated',
          type: 'success'
        })
      );
      navigate(ROUTES.SALES_TARGETS);
    } catch (error) {
      logger.error('Error updating display content:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message:
            'An unknown error occurred while updating display content. Please try again.',
          type: 'error'
        })
      );
    }
  };

  const handleDraftAction = async () => {
    const response = await handleDraft(
      salesTarget,
      salesTarget,
      isPrerequisiteTarget,
      'displayContent',
      {
        displayContentFormData,
        displayPreReqContentFormData
      }
    );

    if (response?.success) {
      navigate(ROUTES.SALES_TARGETS);
    }
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget());
    navigate(ROUTES.SALES_TARGETS);
  };

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel);
  };

  const handleStepClick = (step) => {
    // Persist display content to Redux before navigating so values are retained when user returns
    persistDisplayContentToRedux();

    const stepRouteMap = isLinkedTargetType
      ? {
          1: ROUTES.SALES_TARGET_ADD,
          3: ROUTES.SALES_TARGET_REVIEW
        }
      : {
          1: ROUTES.SALES_TARGET_ADD,
          3: ROUTES.SALES_TARGET_PRODUCTS,
          4: ROUTES.SALES_TARGET_REVIEW
        };

    const route = stepRouteMap[step];
    if (route) {
      navigate(route, {
        state: {
          isEdit,
          disableDraft,
          editPublishedBeforeStart,
          salesTargetId: salesTargetIdFromState || salesTarget?.id
        }
      });
    }
  };

  const getDisplayContentLanguages = () => {
    getDisplayContentLanguagesService().then((res) => {
      dispatch(setLanguages(res));
      setActiveLanguage(res[0].code);
    });
  };

  useEffect(() => {
    setMediaCardFormData({
      title: salesTarget?.displayContentMedia?.title,
      imageId: salesTarget?.displayContentMedia?.imageId,
      imageUri: salesTarget?.displayContentMedia?.imageUri,
      imageBase64: salesTarget?.displayContentMedia?.imageBase64,
      optIn: salesTarget?.displayContentMedia?.optIn,
      imageUrl: salesTarget?.displayContentMedia?.imageUrl
    });
  }, [salesTarget?.displayContentMedia]);

  useEffect(() => {
    if (countryLanguages?.length > 0) {
      dispatch(setLanguages(countryLanguages));
      setActiveLanguage(countryLanguages[0].code);
    } else {
      getDisplayContentLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryLanguages]);
  useEffect(() => {
    if (!salesTarget.targetType) navigate(ROUTES.SALES_TARGET_ADD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      hasInvalidImageCombination(
        displayContentMedia,
        displayContentMediaOptional
      )
    ) {
      setErrorMessageMedia(true);
    } else {
      setErrorMessageMedia(false);
    }
  }, [displayContentMedia, displayContentMediaOptional]);

  useEffect(() => {
    const languages =
      COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
    if (languages?.length > 0) {
      const initialFormData = languages.reduce((acc, lang) => {
        acc[lang.code] = {
          targetType: salesTarget?.targetType,
          ...salesTarget?.displayContent?.[lang.code]
        };
        return acc;
      }, {});
      setDisplayContentFormData(initialFormData);
    }
  }, [
    displayContentLanguages,
    salesTarget?.targetType,
    salesTarget?.displayContent,
    countryLanguages,
    countryCode
  ]);

  useEffect(() => {
    const languages =
      COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
    if (!languages || languages.length === 0) return;
    const updatedParent = {};
    const updatedChild = {};
    languages.forEach((lang) => {
      const code = lang.code;
      updatedParent[code] = {
        targetType: salesTarget?.targetType,
        ...(salesTarget?.displayContent?.[code] || {})
      };
      updatedChild[code] = {
        targetType: salesTarget?.targetType,
        ...(salesTarget?.child?.displayContent?.[code] || {})
      };
    });
    setDisplayPreReqContentFormData({
      ...updatedParent,
      child: updatedChild
    });
  }, [
    displayContentLanguages,
    salesTarget?.targetType,
    salesTarget?.displayContent,
    salesTarget?.child?.displayContent,
    countryLanguages,
    countryCode
  ]);

  const handleTabClick = (langCode) => {
    setActiveLanguage(langCode);
    if (!displayContentFormData[langCode]) {
      const newData = {
        targetType: salesTarget?.targetType,
        title: salesTarget?.displayContent?.[langCode]?.title || null,
        category: salesTarget?.displayContent?.[langCode]?.category || '',
        parentCategory:
          salesTarget?.displayContent?.[langCode]?.parentCategory || '',
        childCategory:
          salesTarget?.displayContent?.[langCode]?.childCategory || '',
        excerpt: salesTarget?.displayContent?.[langCode]?.excerpt || '',
        terms: salesTarget?.displayContent?.[langCode]?.terms || '',
        description: salesTarget?.displayContent?.[langCode]?.description || ''
      };

      setDisplayContentFormData((prevData) => ({
        ...prevData,
        [langCode]: newData
      }));
    }
  };

  const handleTabClickPreReq = (tab) => {
    const languages =
      COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || [];
    const updateLanguages = (existing = {}) => {
      const updated = {...existing};
      languages.forEach(({code}) => {
        if (!updated[code]) {
          updated[code] = {
            targetType: salesTarget?.targetType,
            title: '',
            excerpt: '',
            terms: '',
            category: '',
            ...(tab === 'parent' && {parentCategory: '', childCategory: ''})
          };
        }
      });
      return updated;
    };

    setDisplayPreReqContentFormData((prevData) =>
      tab === 'parent'
        ? updateLanguages(prevData)
        : {...prevData, child: updateLanguages(prevData.child)}
    );
  };

  const isLanguageContentValid = (content) => {
    if (!content) return false;
    const validationFields = isLinkedTargetType
      ? ['titleValid', 'descriptionValid', 'title', 'description']
      : [
          'excerptValid',
          'termsValid',
          'titleValid',
          'title',
          'excerpt',
          'terms'
        ];
    return validationFields.some(
      (field) =>
        content[field] === true ||
        content[field] === undefined ||
        content[field] === ''
    );
  };

  const validateDisplayContent = (displayContentFormData) => {
    return Object.values(displayContentFormData).some((languageContent) =>
      isLanguageContentValid(languageContent)
    );
  };

  const getFormError = () => {
    let isInValid = true;
    const {displayContentMedia, displayContentMediaOptional} = salesTarget;

    // Check if we have at least one valid image (either primary or optional)
    const hasValidPrimaryImage =
      displayContentMedia?.imageUrl && displayContentMedia?.imageUri;
    const hasValidOptionalImage =
      displayContentMediaOptional?.imageUrl &&
      displayContentMediaOptional?.imageUri;

    const hasAnyValidImage = hasValidPrimaryImage || hasValidOptionalImage;
    if (!hasAnyValidImage) {
      return true;
    }
    if (isPrerequisiteTarget) {
      const parentForm = Object.fromEntries(
        Object.entries(displayPreReqContentFormData).filter(
          ([key]) => key !== 'child'
        )
      );
      const isParentInValid = validateDisplayContent(parentForm);
      const isChildInValid = validateDisplayContent(
        displayPreReqContentFormData.child
      );
      isInValid = isParentInValid || isChildInValid;
    } else {
      isInValid = validateDisplayContent(displayContentFormData);
    }

    if (
      hasInvalidImageCombination(
        displayContentMedia,
        displayContentMediaOptional
      )
    ) {
      return true;
    }
    // Check if there's at least one foreground image
    const hasForegroundImage =
      displayContentMedia?.imageId === IMAGETYPE.Foreground ||
      displayContentMediaOptional?.imageId === IMAGETYPE.Foreground;
    if (!hasForegroundImage) {
      return true;
    }
    return isInValid;
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: ROUTES.HOME,
              label: ''
            },
            {
              href: ROUTES.SALES_TARGETS,
              label: 'Loyalty Campaigns'
            },
            {
              href: ROUTES.SALES_TARGETS,
              label: `Add Sales Target`
            }
          ]
        }}
        id={'astdc_bradcrumb'}
      />

      <PageHeader
        title={
          isEditDisplayContent ? 'Edit Display Content' : 'Add Sales Target'
        }
      />

      {!isEditDisplayContent && (
        <ProgressIndicator
          currentStep={2}
          completed={false}
          orientation='horizontal'
          steps={stepConfig.steps}
          titles={stepConfig.titles}
          className='mb-6'
          onStepClick={handleStepClick}
        />
      )}
      <br />
      {isPrerequisiteTarget ? (
        <DisplayPreReqContent
          MediaCardUiSchema={MediaCardUiSchema}
          setDisplayPreReqContentFormData={setDisplayPreReqContentFormData}
          displayPreReqContentFormData={displayPreReqContentFormData}
          mediaCardFormData={mediaCardFormData}
          setMediaCardFormData={setMediaCardFormData}
          handleTabClickPreReq={handleTabClickPreReq}
          languages={
            countryLanguages?.length > 0
              ? countryLanguages
              : displayContentLanguages
          }
          targetType={salesTarget?.targetType}
          errorMessage={errorMessageMedia}
        />
      ) : (
        <DisplayContent
          MediaCardUiSchema={MediaCardUiSchema}
          setDisplayContentFormData={setDisplayContentFormData}
          displayContentFormData={displayContentFormData}
          mediaCardFormData={mediaCardFormData}
          setMediaCardFormData={setMediaCardFormData}
          languages={
            countryLanguages?.length > 0
              ? countryLanguages
              : displayContentLanguages
          }
          handleTabClick={handleTabClick}
          activeLanguage={activeLanguage}
          targetType={salesTarget.targetType}
          joinCampaignFlag={salesTarget?.joinCampaignFlag}
          errorMessage={errorMessageMedia}
        />
      )}
      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text={
            'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          }
          showPrimaryButton={true}
          primaryButtonText='Yes'
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText='No'
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
          isDark={true}
        />
      ) : null}

      {isEditDisplayContent ? (
        <SaveCancelFooter
          FormError={getFormError()}
          handleCancel={() => navigate(returnTo)}
          disableDraft
          draftAction={() => {}}
          nextAction={handleSaveAndPublishEditDisplayContent}
          isReview
        />
      ) : (
        <SaveCancelFooter
          FormError={getFormError()}
          handleCancel={handleCancel}
          draftAction={handleDraftAction}
          nextAction={handleNextAction}
          disableDraft={disableDraft}
        />
      )}
    </>
  );
};
